import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Status } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { GeneratedInsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { differenceInSeconds } from "date-fns";
import maxDates from "date-fns/max";
import { has } from "lodash";
import * as R from "remeda";

import { DEFAULT_ITEM } from "../types/common";
import type { GeneratedInsightsFieldSelections } from "../types/filters/generatedInsights";
import { GeneratedInsightsFilterField } from "../types/filters/generatedInsights";
import type { FieldSelections } from "../types/insightsFilter";

import { equalsDefaultValue } from "./common.utils";
import type { FilterOptionsMap } from "./filters.utils";

export const insightTypeLabelLookup = {
    [GeneratedInsightType.WB]: "Workbench Compiled",
    [GeneratedInsightType.PKG]: "Packaged",
};

export const discoveryInsightTypeLabelLookup = {
    [GeneratedInsightType.WB]: "Workbench decks",
    [GeneratedInsightType.PKG]: "Insight decks",
};

export const getGeneratedInsightsRefreshInterval = (
    insights: GeneratedInsight[],
): number | undefined => {
    const quickIntervalRefreshRateMS = 5000; // 5 seconds;
    const intervalRefreshRateMS = 300000; // 5 minutes;
    const quickIntervalMaxWaitSeconds = 120; // 2 minutes;

    // Find the most recent updateTimestamp as a Date
    const mostRecentDate = R.pipe(
        insights,
        R.map((e) => (e.updateTimestamp ? new Date(e.updateTimestamp) : null)),
        R.filter(R.isDate),
        maxDates,
    );
    // Compare to current Date
    const differenceSeconds = Math.abs(
        differenceInSeconds(mostRecentDate, new Date()),
    );
    if (Number.isNaN(differenceSeconds)) {
        return undefined;
    } else if (differenceSeconds <= quickIntervalMaxWaitSeconds)
        return quickIntervalRefreshRateMS;
    else return intervalRefreshRateMS;
};

export type GeneratedInsightsFilters = {
    [GeneratedInsightsFilterField.BRAND]: FilterOptionsMap;
    [GeneratedInsightsFilterField.BROWSE_NODE]: FilterOptionsMap;
    [GeneratedInsightsFilterField.INSIGHT_TYPE]: FilterOptionsMap;
};

export const generateGeneratedInsightFilterOptions = (
    insights: GeneratedInsight[],
): GeneratedInsightsFilters => {
    const filters: GeneratedInsightsFilters = {
        [GeneratedInsightsFilterField.BRAND]: {},
        [GeneratedInsightsFilterField.BROWSE_NODE]: {},
        [GeneratedInsightsFilterField.INSIGHT_TYPE]: {},
    };

    insights.forEach((insight: GeneratedInsight) => {
        const { brand, brandId, browseNode, browseNodeId, insightType } =
            insight;

        if (
            brandId &&
            brand &&
            !has(filters[GeneratedInsightsFilterField.BRAND], brandId)
        ) {
            filters[GeneratedInsightsFilterField.BRAND][brandId] = {
                label: brand,
                value: brandId,
            };
        }

        if (
            browseNodeId &&
            browseNode &&
            !has(
                filters[GeneratedInsightsFilterField.BROWSE_NODE],
                browseNodeId,
            )
        ) {
            filters[GeneratedInsightsFilterField.BROWSE_NODE][browseNodeId] = {
                label: browseNode,
                value: browseNodeId,
            };
        }

        if (
            insightType &&
            !has(
                filters[GeneratedInsightsFilterField.INSIGHT_TYPE],
                insightType,
            ) &&
            isSupportedGeneratedInsightType(insightType)
        ) {
            filters[GeneratedInsightsFilterField.INSIGHT_TYPE][insightType] = {
                label: discoveryInsightTypeLabelLookup[insightType],
                value: insightType,
            };
        }
    });

    return filters;
};

export const isSupportedGeneratedInsightType = (
    insightType: string,
): boolean => {
    return Object.values(GeneratedInsightType).includes(
        insightType as GeneratedInsightType,
    );
};

export const matchesGeneratedInsightSelections = (
    insight: GeneratedInsight,
    fieldSelections: GeneratedInsightsFieldSelections,
): boolean => {
    const convertedFieldSelections = convertDefaultValues(fieldSelections);
    const unifiedFieldSelections: FieldSelections = {
        brand: convertedFieldSelections.GENERATED_BRAND,
        browseNode: convertedFieldSelections.GENERATED_BROWSE_NODE,
        insightType: convertedFieldSelections.GENERATED_INSIGHT_TYPE,
    };

    return matchesSelections(insight, unifiedFieldSelections);
};

const matchesSelections = (
    insight: GeneratedInsight,
    fieldSelections: FieldSelections,
): boolean => {
    // A match is considered true if any of the following conditions are met
    // 1. The insight has that field defined and that field matches the selected option
    // 2. The default option is selected
    // 3. The insight does not have that field

    const matchesBrand =
        (insight.brand &&
            insight.brandId &&
            insight.brandId === fieldSelections.brand?.value) ||
        fieldSelections.brand?.value === DEFAULT_ITEM.value ||
        !insight.brandId ||
        !insight.brand;

    const matchesBrowseNode =
        (insight.browseNode &&
            insight.browseNodeId &&
            insight.browseNodeId === fieldSelections.browseNode?.value) ||
        fieldSelections.browseNode?.value === DEFAULT_ITEM.value ||
        !insight.browseNodeId ||
        !insight.browseNode;

    const optionalFieldMatches = [
        GeneratedInsightsFilterField.INSIGHT_TYPE,
    ].every((key: string) => {
        let matched = true;
        if (insight[key] !== undefined) {
            matched =
                (insight[key] &&
                    insight[key] === fieldSelections[key]?.value) ||
                fieldSelections[key]?.value === DEFAULT_ITEM.value ||
                !insight[key];
        }
        return matched;
    });

    return matchesBrand && matchesBrowseNode && optionalFieldMatches;
};

function convertDefaultValues(
    fieldSelections: GeneratedInsightsFieldSelections,
) {
    const convertedFieldSelections: GeneratedInsightsFieldSelections = {
        ...fieldSelections,
    };
    for (const key in GeneratedInsightsFilterField) {
        const field = GeneratedInsightsFilterField[key];
        if (equalsDefaultValue(fieldSelections[field])) {
            convertedFieldSelections[field] = DEFAULT_ITEM;
        }
    }
    return convertedFieldSelections;
}

export const isSuccess = (status?: Status): boolean =>
    status === Status.SUCCESS;
